/**
 * PrivilegeMatrix.js
 *
 * DEV_ADMIN-only one-page editor: rows are technologies, columns are the canonical
 * 7 privilege tiers. Ticking/unticking a checkbox controls whether that tier
 * appears in the Privilege Mapping dropdown for that technology.
 *
 * NONE / READ / WRITE are always enabled (locked checkboxes). Unchecking an
 * editable tier cascades on save: every active mapping at that tier collapses to
 * the next-lower enabled tier and is reset to uncertified.
 */

import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../services/api';

const TECH_LABELS = {
  oracle: 'Oracle',
  mssql: 'MSSQL',
  mysql: 'MySQL',
  postgresql: 'PostgreSQL',
  cassandra: 'Cassandra'
};

const PrivilegeMatrix = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [tierOrder, setTierOrder] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [config, setConfig] = useState({});

  // Pending check/uncheck edits, keyed as `${tech}|${tier}`: bool
  const [pending, setPending] = useState({});

  const [showConfirm, setShowConfirm] = useState(false);

  const loadConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiClient.get('/api/databases/privilege-matrix/config/');
      if (resp.data?.success) {
        setTierOrder(resp.data.tier_order || []);
        setTechnologies(resp.data.technologies || []);
        setConfig(resp.data.config || {});
      } else {
        setError(resp.data?.error || 'Failed to load config');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfig(); }, []);

  const editKey = (tech, tier) => `${tech}|${tier}`;

  const isCurrentlyEnabled = (tech, tier) => {
    const cell = config[tech]?.[tier];
    return cell ? cell.is_enabled : false;
  };

  const isLocked = (tech, tier) => {
    const cell = config[tech]?.[tier];
    return cell ? cell.is_locked : false;
  };

  const proposedEnabled = (tech, tier) => {
    const k = editKey(tech, tier);
    return k in pending ? pending[k] : isCurrentlyEnabled(tech, tier);
  };

  const onToggle = (tech, tier) => {
    if (isLocked(tech, tier)) return;
    const current = isCurrentlyEnabled(tech, tier);
    const k = editKey(tech, tier);
    setPending(prev => {
      const next = { ...prev };
      const wantEnabled = !proposedEnabled(tech, tier);
      if (wantEnabled === current) {
        delete next[k]; // toggled back to original — clear pending
      } else {
        next[k] = wantEnabled;
      }
      return next;
    });
  };

  const pendingList = useMemo(
    () => Object.entries(pending).map(([k, v]) => {
      const [tech, tier] = k.split('|');
      return { tech, tier, is_enabled: v };
    }),
    [pending]
  );

  // Cascade preview: for each pending uncheck, walk the (post-edit) enabled set
  // and find the fallback tier; report the rows-affected count from config.
  const cascadePreview = useMemo(() => {
    if (pendingList.length === 0) return [];

    const proposedEnabledSet = {};
    technologies.forEach(t => {
      proposedEnabledSet[t] = new Set(
        tierOrder.filter(name => proposedEnabled(t, name))
      );
    });

    const previews = [];
    pendingList
      .filter(e => !e.is_enabled)
      .forEach(({ tech, tier }) => {
        // Walk down sort_order from the disabled tier
        const idx = tierOrder.indexOf(tier);
        let fallback = 'NONE';
        for (let i = idx - 1; i >= 0; i--) {
          if (proposedEnabledSet[tech].has(tierOrder[i])) {
            fallback = tierOrder[i];
            break;
          }
        }
        const rowCount = config[tech]?.[tier]?.active_row_count || 0;
        previews.push({ tech, disabled_tier: tier, fallback_tier: fallback, rows: rowCount });
      });
    return previews;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingList, tierOrder, technologies, config]);

  const totalCascadeRows = cascadePreview.reduce((s, p) => s + p.rows, 0);

  const onSaveClick = () => {
    if (pendingList.length === 0) return;
    setShowConfirm(true);
  };

  const performSave = async () => {
    setShowConfirm(false);
    setSaving(true);
    setError('');
    setInfo('');
    try {
      const edits = pendingList.map(({ tech, tier, is_enabled }) => ({
        technology: tech,
        tier_name: tier,
        is_enabled
      }));
      const resp = await apiClient.put(
        '/api/databases/privilege-matrix/config/update/',
        { edits }
      );
      if (resp.data?.success) {
        const cascades = resp.data.cascades || [];
        const recalcs = resp.data.recalculations || [];
        const totalRows = cascades.reduce((s, c) => s + c.rows_cascaded, 0);
        const totalUsers = recalcs.reduce((s, r) => s + (r.users_updated || 0), 0);
        const totalRoles = recalcs.reduce((s, r) => s + (r.roles_updated || 0), 0);
        let msg = `Saved ${edits.length} change${edits.length === 1 ? '' : 's'}.`;
        if (cascades.length > 0) {
          msg += ` Cascaded ${totalRows} mapping row${totalRows === 1 ? '' : 's'} (now uncertified).`;
        }
        if (totalUsers > 0 || totalRoles > 0) {
          msg += ` Re-leveled ${totalUsers} user${totalUsers === 1 ? '' : 's'}` +
            ` and ${totalRoles} role${totalRoles === 1 ? '' : 's'} in the access reports.`;
        }
        setInfo(msg);
        setPending({});
        await loadConfig();
      } else {
        setError(resp.data?.error || 'Save failed');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    setPending({});
    setError('');
    setInfo('');
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h3 className="mb-1">
            <i className="fas fa-th me-2 text-primary"></i>
            Privilege Matrix
          </h3>
          <div className="text-muted small">
            Choose which privilege levels appear in the Privilege Mapping dropdown for each
            technology. <strong>NONE</strong>, <strong>READ</strong>, and <strong>WRITE</strong> are
            always enabled. Unchecking a level cascades existing mappings down to the next
            enabled level and resets them to uncertified.
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          <div>{error}</div>
        </div>
      )}
      {info && (
        <div className="alert alert-success d-flex align-items-center" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          <div>{info}</div>
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5 text-muted">
              <div className="spinner-border text-primary" role="status"></div>
              <div className="mt-2">Loading…</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered mb-0 align-middle">
                <thead className="table-dark">
                  <tr>
                    <th style={{ minWidth: '160px' }}>Technology</th>
                    {tierOrder.map(t => (
                      <th key={t} className="text-center">{t}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {technologies.map(tech => {
                    // Per-tech cascade hints, computed live from the proposed-enabled state.
                    const techHints = cascadePreview.filter(p => p.tech === tech);
                    const colspan = tierOrder.length + 1;
                    return (
                      <React.Fragment key={tech}>
                        <tr>
                          <td className="fw-bold">{TECH_LABELS[tech] || tech}</td>
                          {tierOrder.map(tier => {
                            const cell = config[tech]?.[tier];
                            if (!cell) return <td key={tier} className="text-center text-muted">—</td>;
                            const checked = proposedEnabled(tech, tier);
                            const dirty = editKey(tech, tier) in pending;
                            const locked = cell.is_locked;
                            const rowCount = cell.active_row_count;
                            return (
                              <td
                                key={tier}
                                className={`text-center ${dirty ? 'table-warning' : ''}`}
                                style={{ cursor: locked ? 'not-allowed' : 'pointer' }}
                                onClick={() => onToggle(tech, tier)}
                                title={
                                  locked
                                    ? `${tier} is required and cannot be disabled.`
                                    : `${rowCount} active mapping${rowCount === 1 ? '' : 's'} at ${tier}`
                                }
                              >
                                <div className="form-check d-inline-block m-0">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={checked}
                                    disabled={locked || saving}
                                    onChange={() => onToggle(tech, tier)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ transform: 'scale(1.2)' }}
                                  />
                                </div>
                                <div className="small text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                  {locked ? <span className="text-secondary">required</span> : `${rowCount} mapped`}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                        {techHints.length > 0 && (
                          <tr>
                            <td colSpan={colspan} className="bg-light py-2 px-3" style={{ borderTop: 'none' }}>
                              {techHints.map((h, i) => (
                                <div key={i} className="small text-danger fw-bold d-flex align-items-start" style={{ lineHeight: 1.4 }}>
                                  <i className="fas fa-exclamation-triangle me-2 mt-1"></i>
                                  <div>
                                    Disabling {h.disabled_tier} will remap{' '}
                                    {h.rows} existing privilege{h.rows === 1 ? '' : 's'}{' '}
                                    to {h.fallback_tier}
                                    {h.rows > 0 && <> and require re-certification</>}.
                                  </div>
                                </div>
                              ))}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Action bar */}
      <div className="d-flex justify-content-end mt-3">
        <span className="text-muted me-3 align-self-center small">
          {pendingList.length === 0
            ? 'No unsaved changes'
            : <span className="text-warning fw-bold">{pendingList.length} unsaved change{pendingList.length === 1 ? '' : 's'}</span>}
        </span>
        <button
          className="btn btn-outline-secondary me-2"
          onClick={discardChanges}
          disabled={pendingList.length === 0 || saving}
        >
          Discard
        </button>
        <button
          className="btn btn-primary"
          onClick={onSaveClick}
          disabled={pendingList.length === 0 || saving}
        >
          {saving ? (
            <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
          ) : (
            <><i className="fas fa-save me-1"></i>Save changes</>
          )}
        </button>
      </div>

      {/* Save confirmation modal */}
      {showConfirm && (
        <div className="modal fade show" style={{
          display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto'
        }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '700px', width: '100%', margin: '0 auto' }}>
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="fas fa-save me-2"></i>
                  Confirm changes
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowConfirm(false)}></button>
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  You are about to apply <strong>{pendingList.length}</strong> change{pendingList.length === 1 ? '' : 's'} to the privilege matrix.
                </p>
                {cascadePreview.length > 0 ? (
                  <>
                    <div className="alert alert-warning mb-2">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Disabling a level will cascade <strong>{totalCascadeRows}</strong> existing mapping
                      {totalCascadeRows === 1 ? '' : 's'} down to the next enabled level and reset
                      {totalCascadeRows === 1 ? '' : ' them'} to uncertified.
                    </div>
                    <table className="table table-sm table-bordered mb-2">
                      <thead className="table-dark">
                        <tr>
                          <th>Technology</th>
                          <th>Disabling</th>
                          <th>Fallback</th>
                          <th className="text-end">Rows</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cascadePreview.map((p, i) => (
                          <tr key={i}>
                            <td>{TECH_LABELS[p.tech] || p.tech}</td>
                            <td>{p.disabled_tier}</td>
                            <td>{p.fallback_tier}</td>
                            <td className="text-end">{p.rows}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <p className="text-muted small mb-0">No mappings will be cascaded.</p>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowConfirm(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={performSave}>
                  <i className="fas fa-check me-1"></i>Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivilegeMatrix;
